import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { productsApi } from '../services/api';

const SearchSuggestions = ({ query, onSelect, isVisible }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef(null);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [suggestions.length]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!query || query.length < 2) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      try {
        // Get products matching the query
        const products = await productsApi.search(query);
        // Extract unique brands and categories
        const brands = [...new Set(products.map(p => p.brand).filter(Boolean))];
        const categories = [...new Set(products.map(p => p.category_name).filter(Boolean))];

        const suggestionItems = [
          ...brands.slice(0, 3).map(brand => ({ type: 'brand', value: brand, label: `Brand: ${brand}` })),
          ...categories.slice(0, 2).map(category => ({ type: 'category', value: category, label: `Category: ${category}` })),
          ...products.slice(0, 5).map(product => ({
            type: 'product',
            value: product.name,
            label: product.name,
            brand: product.brand,
            image: product.image,
            price: product.min_price || product.price || 0,
            has_variants: product.has_variants
          }))
        ];

        setSuggestions(suggestionItems);
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  const handleSuggestionClick = useCallback((suggestion) => {
    if (suggestion.type === 'brand') {
      onSelect(`brand:${suggestion.value}`);
    } else if (suggestion.type === 'category') {
      onSelect(`category:${suggestion.value}`);
    } else {
      onSelect(suggestion.value);
    }
  }, [onSelect]);

  const handleKeyDown = useCallback((e) => {
    if (!isVisible) return;

    const suggestionItems = suggestions;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev < suggestionItems.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      const selected = suggestionItems[selectedIndex];
      if (selected) handleSuggestionClick(selected);
    } else if (e.key === 'Escape') {
      setSelectedIndex(-1);
    }
  }, [isVisible, suggestions, selectedIndex, handleSuggestionClick]);

  // Global keydown listener for keyboard navigation
  useEffect(() => {
    if (!isVisible) return;

    const handleGlobalKeyDown = (e) => {
      handleKeyDown(e);
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isVisible, suggestions, selectedIndex, handleKeyDown]);

  if (!isVisible || (!loading && suggestions.length === 0)) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-b-lg shadow-lg z-50 max-h-80 overflow-y-auto"
    >
      {loading ? (
        <div className="p-4 text-center text-gray-500">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gold mx-auto"></div>
          <span className="text-sm mt-2 block">Searching...</span>
        </div>
      ) : (
        <div className="py-2">
           {suggestions.map((suggestion, index) => (
             <button
               key={index}
               onClick={() => handleSuggestionClick(suggestion)}
               className={`w-full text-left px-4 py-2 flex items-center gap-3 group transition-colors ${index === selectedIndex ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
             >
               <div className="flex-shrink-0">
                 {suggestion.type === 'brand' && (
                   <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                   </svg>
                 )}
                 {suggestion.type === 'category' && (
                   <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                   </svg>
                 )}
                 {suggestion.type === 'product' && (
                   suggestion.image ? (
                     <img
                       src={suggestion.image}
                       alt={suggestion.label}
                       className="w-10 h-10 rounded-lg object-cover flex-shrink-0 bg-gray-100"
                     />
                   ) : (
                     <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                       <span className="text-gray-400 text-lg">💄</span>
                     </div>
                   )
                 )}
               </div>
               <div className="flex-1 min-w-0">
                 <div className="text-sm font-medium text-gray-900 truncate">
                   {suggestion.label}
                 </div>
                 <div className="flex items-center gap-2">
                   {suggestion.brand && (
                     <p className="text-xs text-gray-400">
                       {suggestion.brand}
                     </p>
                   )}
                   {suggestion.type === 'product' && suggestion.price > 0 && (
                     <p className="text-xs font-semibold text-gold">
                       KSh {Number(suggestion.price).toLocaleString()}
                     </p>
                   )}
                 </div>
               </div>
               <svg className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
               </svg>
             </button>
           ))}
          <div className="border-t border-gray-100 mt-2 pt-2">
            <Link
              to={`/search?q=${encodeURIComponent(query)}`}
              className="w-full text-left px-4 py-2 text-sm text-gold hover:bg-gray-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              View all results for "{query}"
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchSuggestions;